import { Icon } from './Icon'

export interface FunnelStep {
  key: string
  label: string
  icon?: string
  count: number
}

export function Funnel({ steps }: { steps: FunnelStep[] }) {
  if (steps.length === 0) return null
  const top = steps[0].count || 1
  let worstIdx = -1
  let worstDrop = 0
  for (let i = 1; i < steps.length; i++) {
    const drop = (steps[i - 1].count - steps[i].count) / Math.max(1, steps[i - 1].count)
    if (drop > worstDrop) { worstDrop = drop; worstIdx = i }
  }
  return (
    <div className="funnel" role="img" aria-label={`Embudo: ${steps.map((s) => `${s.label} ${s.count}`).join(', ')}`}>
      {steps.map((s, i) => {
        const widthPct = Math.max(8, (s.count / top) * 100)
        const pctOfTop = ((s.count / top) * 100).toFixed(1)
        const prev = i > 0 ? steps[i - 1].count : null
        // Solo calculamos caída si el paso anterior tuvo volumen (evita "−null%" con prev=0).
        const dropPct = prev && prev > 0 ? (((prev - s.count) / prev) * 100).toFixed(1) : null
        return (
          <div key={s.key} className={`funnel-step ${i === worstIdx ? 'worst' : ''}`}>
            <div className="step-label">
              <span className="step-idx">{i + 1}</span>
              {s.icon ? <Icon name={s.icon} size={14} /> : null}
              {s.label}
            </div>
            <div className="step-bar-wrap">
              <div className="step-bar" style={{ width: `${widthPct}%` }}>
                {s.count.toLocaleString('es-AR')}
                <span className="pct">{pctOfTop}%</span>
              </div>
            </div>
            <div className="step-drop">
              {prev === null || dropPct === null
                ? <span style={{ opacity: 0.5 }}>—</span>
                : <><span className="drop-pct">{'−' + dropPct + '%'}</span><div style={{ fontSize: 11 }}>vs paso anterior</div></>}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export interface TimePoint {
  label: string
  value: number
}

export function TimeSeriesChart({ data, width = 720, height = 220, color = '#15B868' }: {
  data: TimePoint[]
  width?: number
  height?: number
  color?: string
}) {
  const pad = { top: 16, right: 14, bottom: 28, left: 48 }
  const w = width - pad.left - pad.right
  const innerH = height - pad.top - pad.bottom
  const values = data.map((d) => d.value)
  const max = Math.max(...values, 0) * 1.1 || 1
  const step = data.length > 1 ? w / (data.length - 1) : w
  const pts = values.map((v, i) => [pad.left + i * step, pad.top + innerH - (v / max) * innerH] as const)
  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ')
  const areaPath = pts.length
    ? linePath + ` L ${pts[pts.length - 1][0]} ${pad.top + innerH} L ${pts[0][0]} ${pad.top + innerH} Z`
    : ''
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({ value: Math.round(max * t), y: pad.top + innerH - t * innerH }))
  const xLabelIdxs = data.map((_, i) => i).filter((i) => i % 5 === 0 || i === data.length - 1)
  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" className="timeseries-svg" role="img" aria-label={`Serie temporal: ${data.length} puntos, máximo ${Math.max(...values, 0).toLocaleString('es-AR')}`}>
      {yTicks.map((tk, i) => <line key={`g${i}`} className="grid-line" x1={pad.left} x2={width - pad.right} y1={tk.y} y2={tk.y} />)}
      {areaPath ? <path className="area" d={areaPath} fill={color} opacity={0.12} /> : null}
      <path className="line" d={linePath} stroke={color} fill="none" strokeWidth={2} />
      {pts.map((p, i) => <circle key={`p${i}`} className="point" cx={p[0]} cy={p[1]} r={2.5} fill="#fff" stroke={color} strokeWidth={1.5} />)}
      {yTicks.map((tk, i) => <text key={`yl${i}`} className="axis-text" x={pad.left - 8} y={tk.y + 3} textAnchor="end">{tk.value.toLocaleString('es-AR')}</text>)}
      {xLabelIdxs.map((i) => <text key={`xl${i}`} className="axis-text" x={pad.left + i * step} y={height - 8} textAnchor="middle">{data[i].label}</text>)}
    </svg>
  )
}
